import { Dispatch, FC, SetStateAction } from 'react';
import SettingsPanelGroup from '../SettingsPanelGroup/SettingsPanelGroup';
import SettingsPanelItem from '../SettingsPanelItem/SettingsPanelItem';
import SettingsPanelSection, {
  SettingsPanelSectionNode,
} from '../SettingsPanelSection/SettingsPanelSection';
import { useLayoutResolveData } from '../../hooks/useLayoutResolveData';
import { Group } from '../../../DatasetForm/types';
import {
  Dataset,
  FormItem,
  FullscreenReferenceType,
  GridInputValue,
  JSONObject,
  LayoutInlineForm,
} from '../../../../../types/types';
import { LayoutHandlerScope } from '../../../../../types/context';

// Normalized group node: the definition group with its child items as an array.
type SettingsPanelGroupNode = Omit<Group, 'items'> & { items: unknown[] };

export interface SettingsPanelResolverProps {
  // Node of the normalized form tree: section array | group | item.
  node: unknown;
  form: LayoutInlineForm;
  data: JSONObject;
  referenceDatasets: Record<string, Dataset>;
  setReferenceDatasets: Dispatch<SetStateAction<Record<string, Dataset>>>;
  disabledFields?: string[];
  getFieldSaveError?: (name: string) => string | null;
  handlerScope?: LayoutHandlerScope;
  onFieldChange: (name: string, value: GridInputValue) => void;
  onFieldDefault?: (name: string, value: GridInputValue) => void;
  onFieldFocus?: (name: string) => void;
  onFieldBlur?: (name: string) => void;
  onOpenFullscreenReference?: (reference: FullscreenReferenceType) => void;
}

// Dispatcher of the normalized form tree — the layout counterpart of the
// dataset form FormItemResolver: a section array renders as tabs, a group as
// a framed block (both recurse back here for their children), anything else
// is a field.
const SettingsPanelItemResolver: FC<SettingsPanelResolverProps> = ({
  node,
  ...shared
}) => {
  const resolveData = useLayoutResolveData(shared.data, shared.handlerScope);

  // Consecutive definition sections arrive merged into one array = one tab view.
  if (Array.isArray(node)) {
    return (
      <SettingsPanelSection
        sections={node as SettingsPanelSectionNode[]}
        {...shared}
      />
    );
  }

  const groupNode = node as SettingsPanelGroupNode;

  if (groupNode.type === 'group') {
    return (
      <SettingsPanelGroup group={groupNode} resolveData={resolveData}>
        {groupNode.items.map((child, index) => (
          <SettingsPanelItemResolver
            key={`${(child as FormItem).name}_${index}`}
            node={child}
            {...shared}
          />
        ))}
      </SettingsPanelGroup>
    );
  }

  const item = node as FormItem;
  const { data, form } = shared;

  return (
    <SettingsPanelItem
      item={item}
      value={item.name ? (data[item.name] as GridInputValue) : undefined}
      visible={item.visible}
      editable={item.editable}
      resolveData={resolveData}
      formDefinition={form}
      formData={data}
      referenceDatasets={shared.referenceDatasets}
      setReferenceDatasets={shared.setReferenceDatasets}
      isDisabled={!!item.name && !!shared.disabledFields?.includes(item.name)}
      saveErrorKey={
        item.name ? (shared.getFieldSaveError?.(item.name) ?? null) : null
      }
      handlerScope={shared.handlerScope}
      onChange={shared.onFieldChange}
      onApplyDefault={shared.onFieldDefault}
      onFieldFocus={shared.onFieldFocus}
      onFieldBlur={shared.onFieldBlur}
      onOpenFullscreenReference={shared.onOpenFullscreenReference}
    />
  );
};

export default SettingsPanelItemResolver;
